import React, { FC, useRef, useState } from 'react';

import {
  Box,
  Center,
  HStack,
  Divider,
  Stack,
  useDisclosure,
} from '@chakra-ui/react';
import { toArray } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useDeepCompareEffect } from 'react-use';
import { colorOpacity, customTheme } from 'theme';

import { actions } from 'app/containers/PolicyHub/Blueprints/slice';
import {
  getIconColor,
  renderSeverityBar,
  StackedHeader,
  WithResourceIcon,
} from 'components/DataDisplay';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { DetailsField } from 'components/DataDisplay/Utils/DetailsField';
import { ActionButton } from 'components/DataEntry';
import {
  CheckmarkCircleOutlineIcon,
  CloseCircleOutlineIcon,
  IconTypes,
} from 'components/Icons';
import { Drawer } from 'components/Overlay';
import { useEnvironmentOptions } from 'containers/App/hooks/useEnvironmentOptions';
import { ResourceExceptions } from 'containers/PolicyHub/Blueprints/Components/Blueprint/Exceptions/ResourceExceptions';
import {
  renderClouds,
  renderExpiresIn,
} from 'containers/PolicyHub/Blueprints/Components/Blueprint/Exceptions/utils';
import { selectBlueprints } from 'containers/PolicyHub/Blueprints/selectors';
import { useCloudAccountId } from 'containers/Setup/CloudAccounts/utils';

interface Props {
  row: Record<string, any>;
  isOpen: boolean;
  onClose: () => void;
}

export const ExceptionsByEnvironment: FC<Props> = props => {
  const { row, isOpen, onClose } = props;
  const { exceptionsByEnvironment } = useSelector(selectBlueprints);

  const dispatch = useDispatch();
  const [currentRecord, setCurrentRecord] = useState<Record<string, any>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const ref = useRef(document.querySelector('.portal-container'));
  const { environmentMapping } = useEnvironmentOptions();
  const { accountMapping } = useCloudAccountId();
  const params = useParams<{ id: string }>();
  const resourceExceptionsDisclosure = useDisclosure();

  useDeepCompareEffect(() => {
    dispatch(
      actions.getExceptionsByEnvironment({
        q: {
          blueprintId: params?.id!,
          page: exceptionsByEnvironment.page.info.page_number,
          size: exceptionsByEnvironment.page.info.page_size,
          policyId: row?.policy_id,
        },
      }),
    );
  }, [
    row,
    dispatch,
    searchTerm,
    exceptionsByEnvironment.page.info,
    params?.id,
  ]);

  const columns = [
    {
      header: <Box pl="thLeftPaddingWithIcon">Environment</Box>,
      accessor: 'environment_id',
      render: ({ value }) => {
        const env = environmentMapping?.[value];
        const color = getIconColor(row.native_resource);
        return (
          <WithResourceIcon
            resourceType={IconTypes.Environment}
            bgColor={colorOpacity(color, 0.2)}
            color={color}
            useCustomColor
          >
            {env?.name}
          </WithResourceIcon>
        );
      },
    },
    {
      header: 'Business Critical',
      accessor: 'environment_id',
      align: 'center',
      render: ({ row }) => {
        const env = environmentMapping?.[row?.environment_id];
        return (
          <Center>
            <Center boxSize={5} color="primary">
              {env?.is_business_critical ? (
                <CheckmarkCircleOutlineIcon />
              ) : (
                <CloseCircleOutlineIcon />
              )}
            </Center>
          </Center>
        );
      },
    },
    {
      header: <Box pl={12}>Exceptions In</Box>,
      align: 'left',
      render: ({ row }) => {
        const env = environmentMapping?.[row?.environment_id];
        const accounts = env?.account_ids;
        return renderClouds(
          toArray(accounts),
          'accounts',
          accountMapping,
          true,
        );
      },
    },
    {
      header: 'Expires in',
      align: 'left',
      render: renderExpiresIn,
      styles: { width: '180px', cell: { maxWidth: '180px' } },
    },
    {
      header: 'Entities count',
      accessor: 'resource_count',
      render: ({ value }) => (!!value ? value : 'All'),
      styles: { width: '180px', cell: { maxWidth: '180px' } },
    },
    {
      header: 'Actions',
      accessor: 'resource_count',
      render: ({ value, row }) => (
        <Center>
          <ActionButton
            iconType={IconTypes.List}
            label="Show entities"
            onClick={() => {
              setCurrentRecord(row);
              resourceExceptionsDisclosure.onOpen();
            }}
            isDisabled={!value}
          />
        </Center>
      ),
      styles: { width: '180px', cell: { maxWidth: '180px' } },
    },
  ];

  const fieldStyles = {
    color: customTheme.colors.gray['300'],
    styles: { value: { color: '#000' } },
    spacing: 3,
  };

  return (
    <>
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        header={
          <StackedHeader
            upper={row?.alert_description}
            lower="Exception"
            type={IconTypes.Exception}
          />
        }
        styles={{
          content: { maxW: '1580px' },
          //drawer: { portalProps: { containerRef: ref as any } },
        }}
        closeButton
        body={
          <>
            <Stack w="full" h="full" pt={3} spacing={5}>
              <HStack spacing={10} align="start">
                <Box w="60%">
                  <DetailsField
                    label="Description"
                    isInline={false}
                    width="full"
                    spacing={2}
                    value={
                      <Box
                        border="1px solid"
                        borderColor={customTheme.colors.gray['100']}
                        h={24}
                        w="full"
                        borderRadius={6}
                        py={2}
                        px={3}
                        overflow="auto"
                      >
                        {row.alert_description}
                      </Box>
                    }
                  />
                </Box>
                <Stack spacing={3.5} w="35%">
                  <DetailsField
                    label="Severity"
                    value={renderSeverityBar({
                      value: row?.severity,
                      reversed: true,
                    })}
                    {...fieldStyles}
                  />
                  <DetailsField
                    label="Environments Impacted"
                    value={row?.environment_ids?.length ?? 0}
                    {...fieldStyles}
                  />
                  <DetailsField
                    label="Accounts Impacted"
                    value={row?.account_ids?.length ?? 0}
                    {...fieldStyles}
                  />
                </Stack>
              </HStack>
              <Divider color="gray.200" />
              {/*<HStack justify="space-between">
              <Box w="30%">
                <SearchBox onChange={setSearchTerm} value={searchTerm} />
              </Box>
              <HStack>
                <Box fontWeight={600} color="primary">
                  FILTERS:
                </Box>
              </HStack>
            </HStack>*/}
              <Table
                data={exceptionsByEnvironment.data}
                columns={columns}
                cursor="pointer"
                isLoading={exceptionsByEnvironment.isLoading}
                pagination={{
                  totalCount: exceptionsByEnvironment.page.totalCount,
                  pageInfo: exceptionsByEnvironment.page.info,
                  onChange: pageInfo =>
                    dispatch(
                      actions.updateExceptionsByEnvironmentsPagination(
                        pageInfo,
                      ),
                    ),
                }}
              />
            </Stack>
            {resourceExceptionsDisclosure.isOpen && (
              <>
                <ResourceExceptions
                  exception={row}
                  row={currentRecord}
                  isOpen={resourceExceptionsDisclosure.isOpen}
                  onClose={resourceExceptionsDisclosure.onClose}
                />
              </>
            )}
          </>
        }
      />
    </>
  );
};
